'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';
import { UserType } from '@diritto/common/account/constants/user.constant';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import {
  ContentSerializeStatus,
  ContentStatus,
  RelayType,
  StudioContentsTabType,
} from '@diritto/common/content/constants';
import { SectionPosition } from '@diritto/common/home/constants/section-constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { Typography } from '@diritto/common/style/typography';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { extractEnumValue } from '@diritto/common/utils/enum';
import { useGetSectionsQuery } from '@/queries/relay/useGetSectionsQuery';
import Section from 'app-components/home/sections/Section';
import { MyContentList } from 'app-components/studio/myContents/contents/MyContentList';
import { ListHeader, SectionText } from 'app-components/studio/myContents/ListHeader';
import { StudioContentsTabItem, Tab, TabList } from 'app-components/studio/myContents/Tab';
import { ButtonSize } from 'constants/button';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useGetWsContentsQuery } from 'queries/studio/useGetWsContentsQuery';
import { usePostRelayWorkspaceContentsQuery } from 'queries/studio/usePostRelayWorkspaceContentsQuery';
import { LinkButton } from 'ui-components/common/LinkButton';
import { Loading } from 'ui-components/common/Loading';
import { DirittoStudioLayout } from 'ui-components/layout/DirittoStudioLayout';
import { DirittoStudioLeftMenu } from 'ui-components/navigations/dirittoStudioMenu/DirittoStudioLeftMenu';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { useRequestCertificate } from 'ui-hooks/useRequestCertificate';
import { showAlertModal } from 'utils/modal';

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.DirittoStudioContentAllList,
};

export function StudioMyContentsPage() {
  useInAppHeader({ screen: WebViewScreen.MyContentDetail, hideHeader: true });
  useInAppStatusBar(WebViewScreen.MyContentDetail);

  const router = useRouter();
  const searchParams = useSearchParams();
  const { tracker } = useTrackingState();
  const { user } = useAuthState();
  const requestCertificate = useRequestCertificate();

  const { data: sectionData } = useGetSectionsQuery({
    includeAdult: !!user?.isAdult,
    position: SectionPosition.DirittoStudioMyContentsTop,
  });

  const tabType = useMemo(
    () => extractEnumValue(StudioContentsTabType, String(searchParams?.get('tabType')), StudioContentsTabType.All),
    [searchParams],
  );

  useEffect(() => {
    tracker.track(TrackingEventType.ViewStudioContentList);
  }, [tracker]);

  const handleTrack = useCallback(
    (action: ClickAction, clickType: ClickTargetType, position: TrackerPosition) => {
      tracker.track(TrackingEventType.Click, {
        action,
        clickType,
        trackerExtraInfo: { trackerPosition: position },
      });
    },
    [tracker],
  );

  const getTrackerPosition = useCallback(() => {
    switch (tabType) {
      case StudioContentsTabType.Serialized:
        return TrackerPosition.DirittoStudioContentSerializedList;
      case StudioContentsTabType.CompletedOrPublished:
        return TrackerPosition.DirittoStudioContentCompletedOrPublishedList;
      default:
        return TrackerPosition.DirittoStudioContentAllList;
    }
  }, [tabType]);

  const handleAddContentClick = useCallback(() => {
    if (!user?.ageVerified) {
      showAlertModal(
        { title: '본인인증을 해주세요.', description: '해당 기능을 사용하려면 본인인증이 필요해요.' },
        {
          onConfirm: requestCertificate,
          confirmText: '본인인증하러 가기',
          cancelText: '닫기',
        },
      );
      return;
    }
    handleTrack(ClickAction.ToDirittoStudioContentAdd, ClickTargetType.Button, getTrackerPosition());
    router.push('/studio/contents/add');
  }, [handleTrack, requestCertificate, router, user?.ageVerified, getTrackerPosition]);

  const { data: wsContentsRes, isPending: isWsContentsPending } = useGetWsContentsQuery();
  const contentIds = useMemo(() => wsContentsRes?.contentIds ?? [], [wsContentsRes?.contentIds]);
  const { data: relayContentRes, isPending: isRelayContentPending } = usePostRelayWorkspaceContentsQuery({
    contentIds,
    includeAdult: true,
  });
  const isLoading = isWsContentsPending || (contentIds.length > 0 && isRelayContentPending);

  const contentsCount = useMemo(
    () =>
      contentIds.reduce(
        (acc, contentId) => {
          const content = relayContentRes?.contents[contentId];
          if (!content || content.relayType !== RelayType.Public) {
            return acc;
          }
          acc.allContents.push(content);

          if (
            content.status === ContentStatus.Public &&
            content.serializeStatus === ContentSerializeStatus.Serialized
          ) {
            acc.serializedContents.push(content);
          } else if (
            [ContentSerializeStatus.Completed, ContentSerializeStatus.Published].includes(content.serializeStatus)
          ) {
            acc.completedOrPublishedContents.push(content);
          }

          return acc;
        },
        {
          serializedContents: [] as ContentPublicRelayDto[],
          completedOrPublishedContents: [] as ContentPublicRelayDto[],
          allContents: [] as ContentPublicRelayDto[],
        },
      ),
    [contentIds, relayContentRes?.contents],
  );

  const contents = useMemo(
    () =>
      ({
        [StudioContentsTabType.Serialized]: contentsCount.serializedContents,
        [StudioContentsTabType.CompletedOrPublished]: contentsCount.completedOrPublishedContents,
        [StudioContentsTabType.All]: contentsCount.allContents,
        [StudioContentsTabType.Contest]: [],
      })[tabType] || [],
    [contentsCount, tabType],
  );

  const listHeaderRightArea = [
    <LinkButton
      key="add"
      href="/studio/contents/add"
      label="새 작품"
      onClick={handleAddContentClick}
      size={ButtonSize.XSmall}
      buttonTypography={Typography.XXXSmall}
    />,
  ];

  return (
    <DirittoStudioLayout leftMenu={<DirittoStudioLeftMenu />}>
      {sectionData && sectionData.sections.length > 0 && (
        <div className="py-4 flex flex-col">
          {sectionData.sections.map(section => (
            <Section
              key={section.id}
              section={section}
              contents={sectionData.contents}
              episodes={sectionData.episodes}
              profileLites={sectionData.profileLites || {}}
              replies={sectionData.replies}
              fancyShares={sectionData.fancyShares}
              rollings={sectionData.rollings}
              trackerExtraInfo={trackerExtraInfo}
            />
          ))}
        </div>
      )}

      {user?.type !== UserType.Partner && (
        <ListHeader
          className="z-[var(--z-index-navbar)]"
          leftArea={<SectionText className="text-lg font-bold">내 작품</SectionText>}
          rightArea={listHeaderRightArea}
        />
      )}
      <Tab
        leftTabs={
          <TabList gap={32}>
            <StudioContentsTabItem
              selectedTabType={tabType}
              tabType={StudioContentsTabType.All}
              text={`전체 ${contentsCount.allContents.length}`}
            />
            <StudioContentsTabItem
              selectedTabType={tabType}
              tabType={StudioContentsTabType.Serialized}
              text={`연재 중 ${contentsCount.serializedContents.length}`}
            />
            <StudioContentsTabItem
              selectedTabType={tabType}
              tabType={StudioContentsTabType.CompletedOrPublished}
              text={`완결 / 출간 ${contentsCount.completedOrPublishedContents.length}`}
            />
          </TabList>
        }
      />
      {isLoading && <Loading />}
      {!isLoading && (
        <MyContentList
          tabType={tabType}
          onAddContent={handleAddContentClick}
          contents={contents as ContentPublicRelayDto[]}
          user={user}
        />
      )}
    </DirittoStudioLayout>
  );
}
